import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Chip, IconButton, Button,
  Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Alert,
} from '@mui/material';
import {
  Star as TargetIcon,
  ExpandMore as ExpandMoreIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as PushIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import { useFileWebSocket } from '../../hooks/useFileWebSocket';
import { useObservationSelection } from '../../hooks/useObservationSelection';
import SelectionBar from './SelectionBar';
import FileGrid from './FileGrid';
import { computeGroupStats, sumFileBytes } from '../../utils/fileStats';
import { formatSize } from '../../utils/fileUtils';
import { getAccessToken } from '../../services/authStorage';

const fileIdsOf = (files) => (files || []).map((f) => f._id || f.file_id); // server bulk-delete matches _id

const API_BASE_URL = '/api/v1';

const nestedAccordionSx = {
  '&:before': { display: 'none' },
  boxShadow: 'none',
  borderTop: '1px solid',
  borderColor: 'divider',
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    return formatInTimeZone(new Date(date), 'UTC', 'yyyy-MM-dd HH:mm') + ' UTC';
  } catch { return 'N/A'; }
};

const groupFilesByObservation = (files) => {
  const byObs = {};
  (files || []).forEach(f => {
    const obsId = f.observation_id?.toString() || 'unknown';
    if (!byObs[obsId]) byObs[obsId] = { name: f.observation_name || obsId, files: [], date: f.uploaded_at };
    byObs[obsId].files.push(f);
  });
  return byObs;
};

const TargetGroupList = ({ searchTerm, onDownloadGroup, onPushGroup, onPreview, getDownloadUrl, getCalibratedDownloadUrl, downloadCalibrated, onCalibrateError, onStatsLoaded, onDelete, canDelete, onBulkDelete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedTargets, setExpandedTargets] = useState(new Set());
  const [expandedObservations, setExpandedObservations] = useState(new Set());
  const [loadedFiles, setLoadedFiles] = useState({});
  const [masterCoverage, setMasterCoverage] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(new Set());
  const loadedFilesRef = useRef(loadedFiles);
  loadedFilesRef.current = loadedFiles;
  const selectionEnabled = !!onBulkDelete;
  const selection = useObservationSelection();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/user-files/by-target`, {
        headers: { Authorization: `Bearer ${token}` },
        params: searchTerm ? { search: searchTerm } : {}
      });
      if (response.data.success) {
        setData(response.data.data);
        setLoadedFiles({});
        if (onStatsLoaded && response.data.data?.groups) {
          onStatsLoaded(computeGroupStats(response.data.data.groups, { observations: response.data.data.total_observations }));
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, onStatsLoaded]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGroupFiles = useCallback(async (targetName) => {
    if (loadedFilesRef.current[targetName]) return;
    setLoadingFiles(prev => new Set(prev).add(targetName));
    try {
      const token = getAccessToken();
      const params = { groupType: 'target', groupId: targetName };
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API_BASE_URL}/user-files/group-files`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (response.data.success) {
        setLoadedFiles(prev => ({ ...prev, [targetName]: response.data.data.files }));
        if (response.data.data.master_coverage) {
          setMasterCoverage(prev => ({ ...prev, ...response.data.data.master_coverage }));
        }
      }
    } catch (err) {
      console.error('Failed to load files for target:', targetName, err);
    } finally {
      setLoadingFiles(prev => {
        const s = new Set(prev);
        s.delete(targetName);
        return s;
      });
    }
  }, [searchTerm]);

  const handleFileUpdate = useCallback((update) => {
    setLoadedFiles(prev => {
      // Object-wrap the mutation flag so the map() callback below doesn't
      // trip eslint no-loop-func (closing over a let in a for loop).
      const flag = { updated: false };
      const next = {};
      for (const [key, files] of Object.entries(prev)) {
        const updatedFiles = files.map(file => {
          const fileId = file.file_id || file._id;
          if (fileId === update.file_id) {
            flag.updated = true;
            return { ...file, plate_solve_status: update.plate_solve_status, plate_solve_result: update.plate_solve_result || file.plate_solve_result };
          }
          return file;
        });
        next[key] = updatedFiles;
      }
      return flag.updated ? next : prev;
    });
  }, []);

  useFileWebSocket(handleFileUpdate);

  const handleSolveFile = async (fileId) => {
    try {
      const token = getAccessToken();
      await axios.post(`${API_BASE_URL}/user-files/${fileId}/plate-solve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { alert('Failed to queue plate solving job'); }
  };

  const toggleTarget = (targetName) => {
    setExpandedTargets(prev => {
      const s = new Set(prev);
      if (s.has(targetName)) {
        s.delete(targetName);
      } else {
        s.add(targetName);
        fetchGroupFiles(targetName);
      }
      return s;
    });
  };

  const toggleSet = (setter, key) => {
    setter(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data?.groups?.length) {
    return <Typography color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>No targets with files found</Typography>;
  }

  return (
    <Stack spacing={2}>
      {selectionEnabled && (
        <SelectionBar
          count={selection.size}
          onClear={selection.clear}
          onDelete={() => onBulkDelete(selection.getSelectedFileIds())}
        />
      )}
      {data.groups.map((group) => {
        const groupId = group._id || group.target_name;
        const files = loadedFiles[groupId];
        const isLoadingFiles = loadingFiles.has(groupId);
        const obsGroups = files ? groupFilesByObservation(files) : {};
        const solvedCount = group.solved_count || 0;
        // Ordered, selectable observation rows for this target (range scope is
        // confined to this group). A row is selectable only if the user may
        // delete all of its files.
        const sortedObs = Object.entries(obsGroups)
          .sort(([, a], [, b]) => (b.date || '').localeCompare(a.date || ''));
        const selectableObs = sortedObs.filter(([, obsData]) =>
          !canDelete || obsData.files.every((f) => canDelete(f)));
        const orderedKeys = selectableObs.map(([obsId]) => `${groupId}-${obsId}`);
        const selectableIndex = new Map(orderedKeys.map((k, i) => [k, i]));
        const fileIdsByKey = Object.fromEntries(
          selectableObs.map(([obsId, obsData]) => [`${groupId}-${obsId}`, fileIdsOf(obsData.files)]));

        return (
          <Accordion
            key={groupId}
            expanded={expandedTargets.has(groupId)}
            onChange={() => toggleTarget(groupId)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <TargetIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">{group.target_name || group._id}</Typography>
                  {(group.ra || group.dec) && (
                    <Typography variant="caption" color="text.secondary">
                      RA: {group.ra || '-'} / Dec: {group.dec || '-'}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`${group.observation_count} obs`} size="small" />
                  <Chip label={`${group.total_files} files`} size="small" />
                  <Chip label={formatSize(group.total_size)} size="small" variant="outlined" />
                  {solvedCount > 0 && (
                    <Chip label={`${solvedCount} solved`} size="small" color="success" variant="outlined" />
                  )}
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => { e.stopPropagation(); onDownloadGroup('target', group._id, group._id); }}
                    title={`Download all files for ${group._id}`}
                  >
                    <DownloadIcon />
                  </IconButton>
                  {onPushGroup && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); onPushGroup('target', group._id, group._id); }}
                      title={`Push all files for ${group._id} to external storage`}
                    >
                      <PushIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isLoadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !files ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                  No files for this target
                </Typography>
              ) : Object.keys(obsGroups).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                  No files for this target
                </Typography>
              ) : (
                <Box>
                  {sortedObs.map(([obsId, obsData]) => {
                      const obsKey = `${groupId}-${obsId}`;
                      const selectable = selectionEnabled && selectableIndex.has(obsKey);
                      return (
                        <Accordion
                          key={obsKey}
                          expanded={expandedObservations.has(obsKey)}
                          // Shift/Ctrl/Cmd-click selects the row; a plain click
                          // expands it as usual.
                          onChange={(e) => {
                            if (selectable && (e.shiftKey || e.ctrlKey || e.metaKey)) {
                              selection.handleClick(groupId, selectableIndex.get(obsKey), orderedKeys, fileIdsByKey, e);
                            } else {
                              toggleSet(setExpandedObservations, obsKey);
                            }
                          }}
                          disableGutters
                          // Lazy-mount the image grid: expanding a target should
                          // only render observation rows, not every file's
                          // preview card at once (memory blow-up on big targets).
                          TransitionProps={{ unmountOnExit: true }}
                          sx={{ ...nestedAccordionSx, ...(selectable && selection.isSelected(obsKey) && { bgcolor: 'action.selected' }) }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ pl: 4, userSelect: 'none' }}
                            title={selectable ? 'Click to expand · Shift/Ctrl-click to select' : undefined}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1">{obsData.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(obsData.date)}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Chip label={`${obsData.files.length} files`} size="small" />
                                <Chip label={formatSize(sumFileBytes(obsData.files))} size="small" variant="outlined" />
                              </Stack>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, pl: 4 }}>
                            <FileGrid
                              files={obsData.files}
                              onPreview={onPreview}
                              onSolve={handleSolveFile}
                              getDownloadUrl={getDownloadUrl}
                              getCalibratedDownloadUrl={getCalibratedDownloadUrl}
                              downloadCalibrated={downloadCalibrated}
                              onCalibrateError={onCalibrateError}
                              masterCoverage={masterCoverage}
                              onDelete={onDelete}
                              canDelete={canDelete}
                            />
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  {group.total_files > (files?.length || 0) && (
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        size="small"
                        onClick={() => navigate(`/files/target/${encodeURIComponent(group._id)}`)}
                      >
                        Showing {files?.length} of {group.total_files} &mdash; View All &rarr;
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default TargetGroupList;
